import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Phone, Mail, Briefcase, Calendar,
  Camera, FileText, Pencil, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";

import { useAuth } from "../../../context/AuthContext";

// Backend file URLs are served from the same origin as the API. Falls back to
// localhost for first-time local setup; production must set REACT_APP_API_URL.
const FILE_HOST = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");

export default function ProfileHeader({ profile }) {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [phone, setPhone] = useState(user?.mobile || "");
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [formData, setFormData] = useState({
    full_name: "",
    title: "",
    current_company: "",
    location: "",
    experience: "0",
    notice_period: "",
  });

  const displayName = user?.name || user?.full_name || profile?.full_name || "";

  // Sync state when user/profile/isEditing changes
  React.useEffect(() => {
    if (user || profile) {
      setPhone(user?.mobile || "");
      setEmail(user?.email || "");
      if (isEditing) {
        setFormData({
          full_name: user?.name || user?.full_name || "",
          title: profile?.designation || "",
          current_company: profile?.current_company || "",
          location: profile?.location || "",
          experience: profile?.experience || "0",
          notice_period: profile?.notice_period || "",
        });
      }
    }
  }, [user, profile, isEditing]);

  // Requirement: Avatar initial from name/full_name
  const nameInitial = (displayName || "U").charAt(0).toUpperCase();

  // ✅ FIX: Profile completion badge, "Profile complete" vs missing count
  const completionPercent = profile?.profile_completion ?? 0;
  const isProfileComplete = completionPercent >= 100 || profile?.isComplete;

  // PHOTO UPLOAD
  const photoMutation = useMutation({
    mutationFn: (fd) => backendApi.entities.UserProfile.uploadPhoto(fd),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["userProfile"]);
      queryClient.invalidateQueries(["me"]);

      // Update context user with new avatar
      if (data.profile_photo) {
        updateUser({ avatar_url: data.profile_photo });
      }

      setIsUploading(false);
      setUploadError("");
    },
    onError: (err) => {
      setIsUploading(false);
      setUploadError(err?.message || "Photo upload failed. Please try again.");
    },
  });

  const handlePhotoClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("photo", file);
    setIsUploading(true);
    setUploadError("");
    photoMutation.mutate(fd);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Send both field name variants, backend accepts title OR designation,
      // full_name OR name, so this works regardless of backend version
      const res = await backendApi.entities.UserProfile.updateHeaderInfo({
        full_name: formData.full_name,
        name: formData.full_name,
        designation: formData.title,
        title: formData.title,
        location: formData.location,
        experience: String(formData.experience),
        notice_period: formData.notice_period,
        current_company: formData.current_company,
      });

      // Update local storage and context
      const updatedUser = res?.user || { ...user, name: formData.full_name, full_name: formData.full_name };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      updateUser(updatedUser);

      // Invalidate queries to trigger re-fetch
      queryClient.invalidateQueries(["userProfile"]);
      queryClient.invalidateQueries(["me"]);
      setIsEditing(false);
      alert("Profile updated successfully ✓");
    } catch (err) {
      const message = err?.message || "Unknown error";
      console.error("Profile update failed:", err);

      if (err?.status === 401) {
        alert("Session expired. Redirecting to login to get a fresh token.");
        logout();
        navigate("/jobs/auth", { replace: true });
        return;
      } else {
        alert(`Failed to update profile: ${message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveContact = async () => {
  try {
    setLoading(true);
    await backendApi.entities.UserProfile.updateContactInfo({
      phone,
      email,
    });
    queryClient.invalidateQueries(["me"]);
    queryClient.invalidateQueries(["userProfile"]);
    setEditContact(false);
    alert("Contact info updated successfully");
  } catch (err) {
    console.error(err);
    alert("Failed to update contact info. Please try again.");
  } finally {
    setLoading(false);
  }
};

const saveSummary = async () => {
  const summary = prompt("Enter profile summary:", profile?.profile_summary || "");
  if (summary === null) return;
  try {
    setLoading(true);
    await backendApi.entities.UserProfile.updateSummary({ summary });
    queryClient.invalidateQueries(["userProfile"]);
    alert("Summary updated successfully");
  } catch (err) {
    console.error(err);
    alert("Failed to update summary. Please try again.");
  } finally {
    setLoading(false);
  }
};

// ✅ FIX: Count missing sections from profile data
const getMissingCount = (p) => {
  if (!p) return 0;
  // If backend already sends missingLabels, use that count
  if (p.missingLabels?.length) return p.missingLabels.length;
  // Otherwise compute locally
  let count = 0;
  if (!p.skills?.length) count++;
  if (!p.education?.length) count++;
  if (!p.projects?.length) count++;
  if (!p.profile_summary) count++;
  if (!p.employment?.length) count++;
  if (!p.resume_url) count++;
  return count;
};

const missingCount = getMissingCount(profile);

const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({ ...prev, [name]: value }));
};

return (
  <div className="bg-white rounded-xl shadow-sm p-6 relative">
    <div className="flex flex-col md:flex-row gap-6">

      {/* PROFILE PHOTO */}
      <div className="flex-shrink-0">
        <div className="relative">
          <input
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
          <div
            onClick={handlePhotoClick}
            className="w-32 h-32 rounded-full bg-gray-100 border-4 border-green-500 flex items-center justify-center overflow-hidden cursor-pointer relative group"
          >
            {(profile?.profile_photo || user?.avatar_url) ? (
              <img
                src={
                  profile?.profile_photo
                    ? `${FILE_HOST}${profile.profile_photo}`
                    : user.avatar_url
                }
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              // ✅ FIX: Shows correct first letter, works for Gmail users too
              <span className="text-gray-400 text-5xl font-semibold">
                {nameInitial}
              </span>
            )}

            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-center p-2">
              {isUploading ? (
                <span className="text-xs animate-pulse">Uploading...</span>
              ) : (
                <>
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs font-medium">Update Photo</span>
                </>
              )}
            </div>
          </div>

          {/* ✅ FIX: Shows actual completion % from backend */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
            {completionPercent}%
          </div>
        </div>

        {/* ✅ FIX: Upload error shown below avatar, not as a blocking alert */}
        {uploadError && (
          <p className="text-red-500 text-xs mt-4 text-center max-w-[128px]">
            {uploadError}
          </p>
        )}
      </div>

      {/* PROFILE INFO */}
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div>
            {/* ✅ FIX: displayName works for Gmail AND email login */}
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              {displayName || ""}
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-400 hover:text-blue-600"
                title="Edit profile"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </h2>

            <p className="text-gray-600 mt-1">
              {profile?.designation || "Professional"}
            </p>

            <p className="text-gray-500 text-sm">
              {profile?.current_company ? `at ${profile.current_company}` : ""}
              <span className="text-gray-400 ml-2">
                Profile last updated,{" "}
                {profile?.updated_at
                  ? new Date(profile.updated_at).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  : "Today"}
              </span>
            </p>

            {/* ✅ FIX: Profile complete / missing details banner */}
            {isProfileComplete ? (
              <div className="mt-2 flex items-center gap-1 text-green-600 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Profile complete
              </div>
            ) : missingCount > 0 ? (
              <div className="mt-2 text-orange-500 text-sm">
                {missingCount} section{missingCount > 1 ? "s" : ""} missing, <button
                  onClick={() => setIsEditing(true)}
                  className="ml-1 underline hover:text-orange-600"
                >
                  complete your profile
                </button>
              </div>
            ) : null}
          </div>

          <div className="hidden lg:flex gap-4">
            <div
              onClick={handlePhotoClick}
              className="border border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 cursor-pointer w-28"
            >
              <Camera className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <p className="text-[11px] text-gray-600 leading-tight">Add photo</p>
              <span className="text-[10px] text-green-600 font-bold">+5%</span>
            </div>

            <div
              onClick={saveSummary}
              className="border border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 cursor-pointer w-28"
            >
              <FileText className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <p className="text-[11px] text-gray-600 leading-tight">Add profile summary</p>
              <span className="text-[10px] text-green-600 font-bold">+8%</span>
            </div>
          </div>
        </div>

        {/* CONTACT INFO GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 mt-6">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <MapPin className="w-4 h-4 text-gray-400" />
            {profile?.location || "Location not set"}
          </div>

          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Phone className="w-4 h-4 text-gray-400" />
            {editContact ? (
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-8 text-xs w-32"
              />
            ) : (
              <span className="flex items-center gap-1">
                {user?.mobile || "Add phone"}
                {user?.mobile && (
                  <span className="w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-[10px]">✓</span>
                  </span>
                )}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Briefcase className="w-4 h-4 text-gray-400" />
            {profile?.experience || "0"} Years Experience
          </div>

          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Mail className="w-4 h-4 text-gray-400" />
            {editContact ? (
              <div className="flex items-center gap-2">
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-8 text-xs w-48"
                />
                <Button
                  onClick={saveContact}
                  size="sm"
                  className="h-7 text-[10px] bg-green-500 hover:bg-green-600"
                >
                  Save
                </Button>
                <Button
                  onClick={() => setEditContact(false)}
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[10px]"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <span className="flex items-center gap-1">
                {user?.email}
                <span className="w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-[10px]">✓</span>
                </span>
                <button
                  onClick={() => setEditContact(true)}
                  className="ml-2 text-blue-500 hover:underline text-[10px]"
                >
                  Edit
                </button>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Calendar className="w-4 h-4 text-gray-400" />
            Available to join in {profile?.notice_period || "Immediate"}
          </div>
        </div>

        {/* ✅ FIX: "Add missing details" button only shows when profile is incomplete */}
        {!isProfileComplete && missingCount > 0 && (
          <Button
            className="mt-6 bg-orange-500 hover:bg-orange-600 rounded-full h-9 px-6 text-sm"
            onClick={() => setIsEditing(true)}
          >
            Add {missingCount} missing detail{missingCount > 1 ? "s" : ""}
          </Button>
        )}
      </div>
    </div>

    {/* EDIT DIALOG */}
    <Dialog open={isEditing} onOpenChange={setIsEditing}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Basic details</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full name</Label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Ex: John Doe"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Current designation</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Software Engineer"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="current_company">Current company</Label>
            <Input
              id="current_company"
              name="current_company"
              value={formData.current_company}
              onChange={handleChange}
              placeholder="Ex: Google"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Current location</Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Ex: Hyderabad, India"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="experience">Total experience (Years)</Label>
            <Input
              id="experience"
              name="experience"
              type="number"
              value={formData.experience}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notice_period">Notice period</Label>
            <Input
              id="notice_period"
              name="notice_period"
              value={formData.notice_period}
              onChange={handleChange}
              placeholder="Ex: 30 Days"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
);
}